import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, CalendarCheck, TreePalm as Palmtree, IndianRupee, UserPlus, ClipboardCheck,
  Award, Wallet, FileText, Plus, Search, Trash2, CheckSquare, X, ShieldCheck,
  Briefcase, Loader2, Star, Shield, Upload, Eye, Pencil, Download, TrendingUp, Phone,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Attendance from "./Attendance";
import Roles from "./Roles";

/**
 * HEXA HR — one app for people ops. Attendance and Roles & Hierarchy used to be
 * separate apps and are now tabs here (their old app ids redirect to these tabs).
 *
 *   Directory    — HR profiles + workspace logins (/hr/employees); boss can add employees
 *   Attendance   — today's counts + the full clock-in / team view (Attendance component)
 *   Leaves       — server-side leave requests; approved leave shows as "On leave"
 *   Payroll      — salary records from Accounts, with slip download
 *   Hiring       — candidate pipeline (localStorage)
 *   Onboarding   — employee profile: resume, photo, Aadhaar, PAN, emergency contact, checklist
 *   Performance  — attendance + tasks + reviews → score and increment suggestion
 *   Roles        — workspace role hierarchy (Roles component)
 *   Expenses / Policies — localStorage
 */

const TABS = [
  { k: "directory",   label: "Directory",          icon: Users,          color: "#6366f1" },
  { k: "attendance",  label: "Attendance",         icon: CalendarCheck,  color: "#22c55e" },
  { k: "leaves",      label: "Leaves",             icon: Palmtree,       color: "#f59e0b" },
  { k: "payroll",     label: "Payroll",            icon: IndianRupee,    color: "#10b981" },
  { k: "hiring",      label: "Hiring",             icon: UserPlus,       color: "#3b82f6" },
  { k: "onboarding",  label: "Onboarding",         icon: ClipboardCheck, color: "#a855f7" },
  { k: "performance", label: "Performance",        icon: Award,          color: "#ec4899" },
  { k: "roles",       label: "Roles & Hierarchy",  icon: Shield,         color: "#0f766e" },
  { k: "expenses",    label: "Expenses",           icon: Wallet,         color: "#f97316" },
  { k: "policies",    label: "Policies",           icon: FileText,       color: "#0ea5e9" },
];

const INPUT = "w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800";
const DEPARTMENTS = ["SALES", "MARKETING", "IT", "HR", "FINANCE", "OPERATIONS", "OTHER"];
const DOCS = [
  { k: "resume",  label: "Resume",     accept: ".pdf,.doc,.docx,image/*" },
  { k: "photo",   label: "Photograph", accept: "image/jpeg,image/png,image/webp" },
  { k: "aadhaar", label: "Aadhaar card", accept: ".pdf,image/*" },
  { k: "pan",     label: "PAN card",   accept: ".pdf,image/*" },
];

const errMsg = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function HR({ tab: requestedTab }) {
  const [tab, setTab] = useState(requestedTab || "directory");
  // Opening "Attendance" / "Roles" from elsewhere re-targets an already-open HR window.
  useEffect(() => { if (requestedTab) setTab(requestedTab); }, [requestedTab]);
  const active = TABS.find(t => t.k === tab) || TABS[0];
  const fullBleed = tab === "attendance" || tab === "roles";

  return (
    <div className="hexa-force-light hexa-form-colorful h-full w-full flex flex-col md:flex-row bg-slate-50 text-slate-800" data-testid="hr-app">
      {/* Sidebar — vertical on desktop, horizontal scroll strip on mobile */}
      <aside className="md:w-56 md:shrink-0 bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col">
        <div className="hidden md:flex px-4 py-3 border-b border-slate-100 items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-sm">
            <ShieldCheck size={14} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">HEXA</div>
            <div className="text-sm font-bold text-slate-800 -mt-0.5">Human Resources</div>
          </div>
        </div>
        <nav className="flex md:flex-col overflow-x-auto md:overflow-y-auto md:overflow-x-visible flex-1 p-2 gap-1 md:gap-0.5 md:space-y-0.5" style={{ scrollbarWidth: "none" }}>
          {TABS.map(t => {
            const Icon = t.icon; const on = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`hr-tab-${t.k}`}
                className={`whitespace-nowrap md:w-full text-left px-3 py-1.5 rounded flex items-center gap-2 text-xs transition ${on ? "hexa-tab-active font-semibold shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <Icon size={13} />
                <span className="md:flex-1">{t.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="hidden md:block px-3 py-2 border-t border-slate-100 text-[10px] text-slate-400 mono">
          // hris · unified people ops
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        <header className="px-3 sm:px-6 py-2 sm:py-3 border-b border-slate-200 bg-white flex items-center gap-3">
          <div className="w-8 h-8 rounded flex items-center justify-center text-white" style={{ background: active.color }}>
            <active.icon size={16} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">{active.label}</h1>
            <div className="text-[11px] text-slate-500 mono">// hr · {active.k}</div>
          </div>
        </header>
        <div className={`flex-1 min-h-0 bg-slate-50 ${fullBleed ? "flex flex-col overflow-hidden" : "overflow-y-auto"}`}>
          {tab === "directory"   && <Directory onGoto={setTab} />}
          {tab === "attendance"  && <AttendanceTab />}
          {tab === "leaves"      && <Leaves />}
          {tab === "payroll"     && <Payroll />}
          {tab === "hiring"      && <Hiring />}
          {tab === "onboarding"  && <Onboarding />}
          {tab === "performance" && <Performance />}
          {tab === "roles"       && <div className="flex-1 min-h-0 overflow-y-auto"><Roles /></div>}
          {tab === "expenses"    && <Expenses />}
          {tab === "policies"    && <Policies />}
        </div>
      </main>
    </div>
  );
}

function useIsBoss() {
  const { user } = useAuth();
  return user?.role === "boss" || !!user?.is_super_admin;
}

/* Shared loader for /hr/employees (profiles + logins without a profile). */
function useEmployees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/employees"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { rows, loading, reload: load };
}

/* Opens an uploaded HR document (auth-protected) in a new tab. */
async function openDocument(eid, kind) {
  const win = window.open("", "_blank");
  try {
    const res = await api.get(`/hr/employees/${eid}/documents/${kind}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (win) win.location.href = url; else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (win) win.close();
    alert(errMsg(e, "Could not open the document."));
  }
}

function EmployeePhoto({ emp, size = 40 }) {
  const [src, setSrc] = useState("");
  const has = !!emp?.documents?.photo && !!emp?.id;
  const stamp = emp?.documents?.photo?.uploaded;
  useEffect(() => {
    if (!has) { setSrc(""); return undefined; }
    let url = ""; let cancelled = false;
    api.get(`/hr/employees/${emp.id}/documents/photo`, { responseType: "blob" })
      .then((res) => { if (!cancelled) { url = URL.createObjectURL(res.data); setSrc(url); } })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [has, emp?.id, stamp]);
  if (src) return <img src={src} alt={emp.name} className="rounded-full object-cover border border-slate-200" style={{ width: size, height: size }} />;
  return <Avatar name={emp?.name || emp?.email} size={size} />;
}

/* ─────────────────────────────────────────────────────────────
   Directory — boss sees HR profiles + logins and can add employees.
   Employees see their colleagues from /users/directory.
   ───────────────────────────────────────────────────────────── */
function Directory({ onGoto }) {
  const isBoss = useIsBoss();
  const { rows: hrRows, loading: hrLoading, reload } = useEmployees();
  const [colleagues, setColleagues] = useState([]);
  const [loadingCol, setLoadingCol] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => { if (isBoss) { setLoadingCol(false); return; } (async () => {
    try { const { data } = await api.get("/users/directory"); setColleagues(data || []); }
    catch { setColleagues([]); }
    setLoadingCol(false);
  })(); }, [isBoss]);

  const rows = isBoss
    ? hrRows
    : colleagues.map(u => ({ id: null, name: u.name || u.username, email: u.address, role: u.role, has_profile: false }));
  const loading = isBoss ? hrLoading : loadingCol;

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(u => `${u.name || ""} ${u.email || ""} ${u.department || ""} ${u.designation || ""} ${u.role || ""}`.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="p-3 sm:p-6">
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{filtered.length} employees</div>}
        right={<>
          <SearchBox value={q} onChange={setQ} placeholder="Search name, email, department…" testId="hr-directory-search" />
          {isBoss && <PrimaryButton onClick={() => setEditing({})} testId="hr-employee-add"><Plus size={12} /> Add Employee</PrimaryButton>}
        </>}
      />
      {isBoss && !loading && rows.length <= 1 && (
        <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3" data-testid="hr-directory-empty-hint">
          Your workspace has no employees yet, so Attendance and Performance have nothing to count.
          Click <b>Add Employee</b> and tick <b>Create HEXA login</b> — each employee then signs in with their own HEXA ID and clocks in from HR → Attendance.
        </div>
      )}
      {loading ? <SkeletonRows /> : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-bold px-4 py-2.5">Employee</th>
                <th className="text-left font-bold px-4 py-2.5">Department</th>
                <th className="text-left font-bold px-4 py-2.5">Designation</th>
                <th className="text-left font-bold px-4 py-2.5">Role</th>
                {isBoss && <th className="text-left font-bold px-4 py-2.5">Documents</th>}
                {isBoss && <th className="text-left font-bold px-4 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr key={u.id || u.email || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hr-dir-row-${u.email || u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <EmployeePhoto emp={u} size={28} />
                      <div>
                        <div className="font-semibold text-slate-800">{u.name || "—"}</div>
                        <div className="text-[11px] text-slate-500 mono">{u.email || "no HEXA login"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{u.department || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{u.designation || "—"}</td>
                  <td className="px-4 py-2.5 capitalize text-slate-600">{u.role || "employee"}</td>
                  {isBoss && (
                    <td className="px-4 py-2.5">
                      {u.has_profile ? <DocChips emp={u} /> : <span className="text-[11px] text-slate-400">No HR profile</span>}
                    </td>
                  )}
                  {isBoss && (
                    <td className="px-4 py-2.5 text-right">
                      {u.has_profile ? (
                        <IconBtn onClick={async () => { try { const { data } = await api.get(`/hr/employees/${u.id}`); setEditing(data); } catch (e) { alert(errMsg(e, "Could not load employee")); } }} title="Edit profile" color="#6366f1" testId={`hr-dir-edit-${u.id}`}><Pencil size={13} /></IconBtn>
                      ) : (
                        <button onClick={() => setEditing({ name: u.name, email: u.email })} className="text-[11px] font-semibold text-indigo-600 hover:underline" data-testid={`hr-dir-create-profile-${u.email}`}>Create profile</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No employees found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {editing && <EmployeeModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function DocChips({ emp }) {
  return (
    <div className="flex flex-wrap gap-1">
      {DOCS.map(d => {
        const has = !!emp.documents?.[d.k];
        return (
          <button key={d.k} disabled={!has} onClick={() => openDocument(emp.id, d.k)}
            title={has ? `Open ${d.label}` : `${d.label} not uploaded`}
            className={`text-[10px] px-1.5 py-0.5 rounded border ${has ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-200 text-slate-400"}`}>
            {has ? "✓ " : ""}{d.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Add / edit employee — used by Directory and Onboarding.
   Files are uploaded after the profile exists (they need its id).
   ───────────────────────────────────────────────────────────── */
function EmployeeModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => ({
    name: "", email: "", phone: "", department: "", designation: "", employee_code: "",
    postal_address: "", start_date: new Date().toISOString().slice(0, 10), manager: "", monthly_salary: "",
    aadhaar_number: "", pan_number: "", emergency_name: "", emergency_phone: "", emergency_relation: "",
    create_login: false, username: "", temp_password: "",
    ...Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v !== null && v !== undefined)),
  }));
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setError("");
    if (!f.name.trim()) { setError("Employee name is required."); return; }
    const aad = f.aadhaar_number.replace(/\D/g, "");
    if (aad && aad.length !== 12) { setError("Aadhaar number must be 12 digits."); return; }
    if (f.pan_number && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(f.pan_number.replace(/\s/g, ""))) { setError("PAN must look like ABCDE1234F."); return; }
    if (f.create_login && (!f.username.trim() || (f.temp_password || "").length < 6)) { setError("For a HEXA login, enter a username and a temporary password of at least 6 characters."); return; }
    setBusy(true);
    try {
      const payload = { ...f, monthly_salary: f.monthly_salary === "" ? null : Number(f.monthly_salary) };
      ["id", "documents", "steps", "steps_done", "aadhaar_masked", "pan_masked", "has_profile", "workspace", "created", "updated", "created_by", "role"].forEach(k => delete payload[k]);
      let emp;
      if (isEdit) {
        ["create_login", "username", "temp_password"].forEach(k => delete payload[k]);
        ({ data: emp } = await api.patch(`/hr/employees/${initial.id}`, payload));
      } else {
        ({ data: emp } = await api.post("/hr/employees", payload));
      }
      const failed = [];
      for (const d of DOCS) {
        const file = files[d.k];
        if (!file) continue;
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/hr/employees/${emp.id}/documents/${d.k}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { failed.push(`${d.label}: ${errMsg(e, "upload failed")}`); }
      }
      if (failed.length) alert(`Employee saved, but some uploads failed:\n${failed.join("\n")}`);
      onSaved?.(emp);
    } catch (e) {
      setError(errMsg(e, "Could not save employee."));
    }
    setBusy(false);
  };

  return (
    <Modal title={isEdit ? `Edit ${initial.name}` : "Add Employee / Onboarding"} onClose={onClose} wide>
      <Section title="Basic details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full name *"><input className={INPUT} value={f.name} onChange={set("name")} data-testid="hr-emp-name" /></Field>
          <Field label="Employee code"><input className={INPUT} value={f.employee_code} onChange={set("employee_code")} placeholder="EMP-001" /></Field>
          <Field label="Department">
            <select className={INPUT} value={f.department} onChange={set("department")} data-testid="hr-emp-department">
              <option value="">—</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Designation"><input className={INPUT} value={f.designation} onChange={set("designation")} placeholder="Sales Executive" data-testid="hr-emp-designation" /></Field>
          <Field label="Phone"><input className={INPUT} value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Joining date"><input type="date" className={INPUT} value={f.start_date} onChange={set("start_date")} /></Field>
          <Field label="Reporting manager"><input className={INPUT} value={f.manager} onChange={set("manager")} /></Field>
          <Field label="Monthly salary (₹)"><input type="number" min="0" className={INPUT} value={f.monthly_salary} onChange={set("monthly_salary")} /></Field>
        </div>
        <Field label="Residential address"><textarea rows={2} className={INPUT} value={f.postal_address} onChange={set("postal_address")} data-testid="hr-emp-address" /></Field>
      </Section>

      <Section title="HEXA login">
        {isEdit || initial?.email ? (
          <Field label="HEXA ID (email)"><input className={INPUT} value={f.email} onChange={set("email")} placeholder="name@hexawebos.com" /></Field>
        ) : (
          <>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={!!f.create_login} onChange={set("create_login")} data-testid="hr-emp-create-login" />
              Create a HEXA login so this employee can clock in and appear in Attendance
            </label>
            {f.create_login ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Username">
                  <div className="flex items-center">
                    <input className={INPUT} value={f.username} onChange={set("username")} placeholder="rahul" data-testid="hr-emp-username" />
                    <span className="text-[11px] text-slate-500 ml-1 whitespace-nowrap">@hexawebos.com</span>
                  </div>
                </Field>
                <Field label="Temporary password"><input className={INPUT} value={f.temp_password} onChange={set("temp_password")} data-testid="hr-emp-password" /></Field>
              </div>
            ) : (
              <Field label="Existing HEXA ID (optional)"><input className={INPUT} value={f.email} onChange={set("email")} placeholder="name@hexawebos.com" /></Field>
            )}
          </>
        )}
      </Section>

      <Section title="Identity documents">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Aadhaar number"><input className={INPUT} value={f.aadhaar_number} onChange={set("aadhaar_number")} placeholder="1234 5678 9012" inputMode="numeric" data-testid="hr-emp-aadhaar" /></Field>
          <Field label="PAN number"><input className={INPUT} value={f.pan_number} onChange={(e) => setF({ ...f, pan_number: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" data-testid="hr-emp-pan" /></Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DOCS.map(d => (
            <FilePick key={d.k} label={d.label} accept={d.accept} file={files[d.k]} existing={initial?.documents?.[d.k]}
              onPick={(file) => setFiles({ ...files, [d.k]: file })} testId={`hr-emp-file-${d.k}`}
              onView={initial?.id && initial?.documents?.[d.k] ? () => openDocument(initial.id, d.k) : null} />
          ))}
        </div>
      </Section>

      <Section title="Emergency contact">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Contact name"><input className={INPUT} value={f.emergency_name} onChange={set("emergency_name")} data-testid="hr-emp-emergency-name" /></Field>
          <Field label="Relation"><input className={INPUT} value={f.emergency_relation} onChange={set("emergency_relation")} placeholder="Father, spouse…" /></Field>
          <Field label="Contact number"><input className={INPUT} value={f.emergency_phone} onChange={set("emergency_phone")} inputMode="tel" data-testid="hr-emp-emergency-phone" /></Field>
        </div>
      </Section>

      {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="hr-emp-error">{error}</div>}
      <ModalFooter onCancel={onClose} onSave={save} busy={busy} testId="hr-emp-save" />
    </Modal>
  );
}

function FilePick({ label, accept, file, existing, onPick, onView, testId }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-lg px-3 py-2 bg-slate-50">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mono mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 cursor-pointer hover:underline">
          <Upload size={12} /> {file || existing ? "Replace" : "Upload"}
          <input type="file" accept={accept} className="hidden" data-testid={testId}
            onChange={(e) => { const fl = e.target.files?.[0]; if (fl) onPick(fl); e.target.value = ""; }} />
        </label>
        <div className="text-[11px] text-slate-600 truncate flex-1">
          {file ? file.name : existing ? existing.name : <span className="text-slate-400">Not uploaded</span>}
        </div>
        {onView && !file && <button onClick={onView} className="text-slate-500 hover:text-indigo-600" title="View"><Eye size={13} /></button>}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-bold uppercase tracking-widest text-indigo-600 border-b border-slate-100 pb-1">{title}</div>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Attendance — today's counts (same source as the team view) + the
   full Attendance app (clock in/out for everyone, team view for boss).
   ───────────────────────────────────────────────────────────── */
function AttendanceTab() {
  const isBoss = useIsBoss();
  const [totals, setTotals] = useState(null);
  useEffect(() => { if (!isBoss) return; (async () => {
    try { const { data } = await api.get("/attendance/team"); setTotals(data?.totals || null); }
    catch { setTotals(null); }
  })(); }, [isBoss]);

  return (
    <>
      {isBoss && (
        <div className="px-3 sm:px-6 pt-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mono mb-2">Today · Team attendance</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="hr-attendance-totals">
            <StatCard label="Employees" value={totals?.employees ?? "—"} color="#6366f1" icon={Users} />
            <StatCard label="Present" value={totals?.present ?? "—"} color="#22c55e" icon={CheckSquare} />
            <StatCard label="On Leave" value={totals?.on_leave ?? "—"} color="#f59e0b" icon={Palmtree} />
            <StatCard label="Absent" value={totals?.absent ?? "—"} color="#ef4444" icon={X} />
          </div>
          {totals && totals.employees <= 1 && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2">
              Only you are in this workspace. Add employees from <b>HR → Directory → Add Employee</b> (with a HEXA login) — they appear here once they clock in.
            </div>
          )}
        </div>
      )}
      <div className="flex-1 min-h-[520px] mx-3 sm:mx-6 mb-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Attendance />
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   Leaves — stored on the server so the boss sees every request and
   approved leave counts as "On leave" in Attendance / Performance.
   ───────────────────────────────────────────────────────────── */
function Leaves() {
  const isBoss = useIsBoss();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { rows: people } = useEmployees();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/leaves"); setRows(data || []); } catch { setRows([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (payload) => {
    try { await api.post("/hr/leaves", payload); setShowForm(false); load(); }
    catch (e) { alert(errMsg(e, "Could not save leave")); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/hr/leaves/${id}`, { status }); load(); } catch (e) { alert(errMsg(e, "Could not update")); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this leave request?")) return;
    try { await api.delete(`/hr/leaves/${id}`); load(); } catch (e) { alert(errMsg(e, "Could not delete")); }
  };

  return (
    <div className="p-6">
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{rows.length} leave request{rows.length !== 1 ? "s" : ""}</div>}
        right={<PrimaryButton onClick={() => setShowForm(true)} testId="hr-leave-new"><Plus size={12} /> New Leave</PrimaryButton>}
      />
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left font-bold px-4 py-2.5">Employee</th>
              <th className="text-left font-bold px-4 py-2.5">Type</th>
              <th className="text-left font-bold px-4 py-2.5">From → To</th>
              <th className="text-left font-bold px-4 py-2.5">Days</th>
              <th className="text-left font-bold px-4 py-2.5">Reason</th>
              <th className="text-left font-bold px-4 py-2.5">Status</th>
              <th className="text-left font-bold px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7}><SkeletonRows /></td></tr>}
            {!loading && rows.map(r => (
              <tr key={r.id} data-testid={`hr-leave-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">{r.employee}<div className="text-[10px] text-slate-400 mono">{r.employee_email}</div></td>
                <td className="px-4 py-2.5 capitalize">{r.type}</td>
                <td className="px-4 py-2.5 tabular-nums text-slate-600">{r.from_date} → {r.to_date}</td>
                <td className="px-4 py-2.5 tabular-nums font-semibold">{r.days}</td>
                <td className="px-4 py-2.5 text-slate-600 truncate max-w-[280px]" title={r.reason}>{r.reason}</td>
                <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1">
                    {isBoss && r.status === "pending" && (
                      <>
                        <IconBtn onClick={() => setStatus(r.id, "approved")} testId={`hr-leave-approve-${r.id}`} color="#22c55e" title="Approve"><CheckSquare size={12} /></IconBtn>
                        <IconBtn onClick={() => setStatus(r.id, "rejected")} testId={`hr-leave-reject-${r.id}`} color="#ef4444" title="Reject"><X size={12} /></IconBtn>
                      </>
                    )}
                    {(isBoss || r.status === "pending") && <IconBtn onClick={() => remove(r.id)} color="#94a3b8" title="Delete"><Trash2 size={12} /></IconBtn>}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No leave requests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showForm && <LeaveModal onSave={add} onClose={() => setShowForm(false)} isBoss={isBoss} people={people} me={user} />}
    </div>
  );
}

function LeaveModal({ onSave, onClose, isBoss, people, me }) {
  const withLogin = people.filter(p => p.email);
  const [f, setF] = useState({ employee_email: me?.address || "", type: "casual", from_date: "", to_date: "", days: 1, reason: "" });
  const save = () => {
    if (!f.from_date || !f.to_date) return;
    const person = withLogin.find(p => p.email === f.employee_email);
    onSave({ ...f, employee: person?.name || me?.name || f.employee_email });
  };
  return (
    <Modal title="New Leave Request" onClose={onClose}>
      {isBoss ? (
        <Field label="Employee">
          <select className={INPUT} value={f.employee_email} onChange={e => setF({ ...f, employee_email: e.target.value })} data-testid="hr-leave-employee">
            {withLogin.map(p => <option key={p.email} value={p.email}>{p.name} — {p.email}</option>)}
          </select>
        </Field>
      ) : (
        <Field label="Employee"><input className={INPUT} value={me?.name || me?.address || ""} disabled /></Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <select className={INPUT} value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
            <option value="casual">Casual</option>
            <option value="sick">Sick</option>
            <option value="earned">Earned / Vacation</option>
            <option value="unpaid">Unpaid</option>
            <option value="maternity">Maternity</option>
            <option value="paternity">Paternity</option>
          </select>
        </Field>
        <Field label="Days"><input className={INPUT} type="number" min="0.5" step="0.5" value={f.days} onChange={e => setF({ ...f, days: parseFloat(e.target.value) || 0 })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From"><input className={INPUT} type="date" value={f.from_date} onChange={e => setF({ ...f, from_date: e.target.value })} data-testid="hr-leave-from" /></Field>
        <Field label="To"><input className={INPUT} type="date" value={f.to_date} onChange={e => setF({ ...f, to_date: e.target.value })} data-testid="hr-leave-to" /></Field>
      </div>
      <Field label="Reason"><textarea rows={3} className={INPUT} value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} data-testid="hr-leave-reason" /></Field>
      <ModalFooter onCancel={onClose} onSave={save} testId="hr-leave-save" />
    </Modal>
  );
}

/* Payroll — salary records kept in Accounts, with the salary-slip PDF. */
function Payroll() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/accounts/salaries"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  })(); }, []);
  const totals = useMemo(() => rows.reduce((acc, r) => {
    acc.ctc += Number(r.ctc || 0);
    acc.paid += Number(r.paid_amount || 0);
    return acc;
  }, { ctc: 0, paid: 0 }), [rows]);
  const slip = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.get(`/accounts/salaries/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `salary-${r.employee_code || r.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert(errMsg(e, "Could not download slip")); }
    setBusy("");
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Salary records" value={rows.length} color="#6366f1" icon={FileText} />
        <StatCard label="Total CTC" value={`₹${totals.ctc.toLocaleString("en-IN")}`} color="#0ea5e9" icon={IndianRupee} />
        <StatCard label="Net paid" value={`₹${totals.paid.toLocaleString("en-IN")}`} color="#22c55e" icon={CheckSquare} />
      </div>
      <div className="mb-3 text-[11px] text-slate-500">
        Salary slips print the employee&apos;s name, department, designation, address, Aadhaar (masked) and PAN from their HR profile.
        Create slips in <b>Accounts → Employee Salary</b> using the same email as the HR profile.
      </div>
      {loading ? <SkeletonRows /> : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-bold px-4 py-2.5">Employee</th>
                <th className="text-left font-bold px-4 py-2.5">Department</th>
                <th className="text-left font-bold px-4 py-2.5">Period</th>
                <th className="text-right font-bold px-4 py-2.5">CTC</th>
                <th className="text-right font-bold px-4 py-2.5">Net paid</th>
                <th className="text-left font-bold px-4 py-2.5">Slip</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`hr-payroll-${r.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">{r.employee_name || r.employee_email || "—"}<div className="text-[10px] text-slate-400">{r.designation}</div></td>
                  <td className="px-4 py-2.5 text-slate-600">{r.department || "—"}</td>
                  <td className="px-4 py-2.5">{r.period || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">₹{Number(r.ctc || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">₹{Number(r.paid_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5">
                    <IconBtn onClick={() => slip(r)} title="Download salary slip" color="#0ea5e9" testId={`hr-payroll-slip-${r.id}`}>
                      {busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    </IconBtn>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No salary records yet. Add them in the Accounts app.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Hiring — candidate pipeline (Kanban) with localStorage */
const STAGES = [
  { k: "applied",    label: "Applied",    color: "#94a3b8" },
  { k: "screening",  label: "Screening",  color: "#0ea5e9" },
  { k: "interview",  label: "Interview",  color: "#a855f7" },
  { k: "offer",      label: "Offer",      color: "#f59e0b" },
  { k: "hired",      label: "Hired",      color: "#22c55e" },
  { k: "rejected",   label: "Rejected",   color: "#ef4444" },
];

function Hiring() {
  const [rows, setRows] = useLocalArray("hexa_hr_candidates");
  const [showForm, setShowForm] = useState(false);

  const grouped = useMemo(() => Object.fromEntries(STAGES.map(s => [s.k, rows.filter(r => r.stage === s.k)])), [rows]);
  const add = (payload) => { setRows([{ id: `c-${Date.now()}`, ...payload, stage: "applied", created: new Date().toISOString() }, ...rows]); setShowForm(false); };
  const move = (id, stage) => setRows(rows.map(r => r.id === id ? { ...r, stage } : r));
  const remove = (id) => setRows(rows.filter(r => r.id !== id));

  return (
    <div className="p-6">
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{rows.length} candidate{rows.length !== 1 ? "s" : ""}</div>}
        right={<PrimaryButton onClick={() => setShowForm(true)} testId="hr-candidate-new"><Plus size={12} /> Add Candidate</PrimaryButton>}
      />
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(180px, 1fr))` }}>
        {STAGES.map(s => (
          <div key={s.k} className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid={`hr-hiring-col-${s.k}`}>
            <div className="px-3 py-2 text-white font-semibold flex items-center justify-between hexa-on-accent" style={{ background: s.color }}>
              <span className="text-sm tracking-wide">{s.label}</span>
              <span className="text-[11px] bg-black/25 rounded-full w-6 h-6 flex items-center justify-center">{grouped[s.k].length}</span>
            </div>
            <div className="p-2 space-y-2 flex-1 min-h-[220px]">
              {grouped[s.k].map(c => (
                <div key={c.id} data-testid={`hr-candidate-${c.id}`} className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mono truncate">{c.role || "—"}</div>
                  <div className="mt-1.5 flex items-center gap-1">
                    <select value={c.stage} onChange={e => move(c.id, e.target.value)} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 outline-none flex-1 bg-white">
                      {STAGES.map(x => <option key={x.k} value={x.k}>{x.label}</option>)}
                    </select>
                    <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showForm && <CandidateModal onSave={add} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function CandidateModal({ onSave, onClose }) {
  const [f, setF] = useState({ name: "", role: "", email: "", phone: "", source: "linkedin", notes: "" });
  return (
    <Modal title="New Candidate" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Full name"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="hr-candidate-name" /></Field>
        <Field label="Role applying for"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.role} onChange={e => setF({ ...f, role: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Phone"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></Field>
      </div>
      <Field label="Source">
        <select className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.source} onChange={e => setF({ ...f, source: e.target.value })}>
          <option value="linkedin">LinkedIn</option>
          <option value="referral">Referral</option>
          <option value="naukri">Naukri</option>
          <option value="indeed">Indeed</option>
          <option value="direct">Direct application</option>
        </select>
      </Field>
      <Field label="Notes"><textarea rows={3} className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></Field>
      <ModalFooter onCancel={onClose} onSave={() => { if (!f.name.trim()) return; onSave(f); }} testId="hr-candidate-save" />
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
   Onboarding — one card per HR profile: photo, documents (resume,
   Aadhaar, PAN), emergency contact and the onboarding checklist.
   ───────────────────────────────────────────────────────────── */
function Onboarding() {
  const isBoss = useIsBoss();
  const { rows, loading, reload } = useEmployees();
  const [editing, setEditing] = useState(null);
  const profiles = rows.filter(r => r.has_profile);

  const toggle = async (emp, sid) => {
    const steps = emp.steps.map(s => s.id === sid ? { ...s, done: !s.done } : s);
    try { await api.patch(`/hr/employees/${emp.id}`, { steps }); reload(); }
    catch (e) { alert(errMsg(e, "Could not update checklist")); }
  };
  const remove = async (emp) => {
    if (!window.confirm(`Delete the HR profile for ${emp.name}? Their HEXA login (if any) is not affected.`)) return;
    try { await api.delete(`/hr/employees/${emp.id}`); reload(); } catch (e) { alert(errMsg(e, "Could not delete")); }
  };
  const edit = async (emp) => {
    try { const { data } = await api.get(`/hr/employees/${emp.id}`); setEditing(data); }
    catch (e) { alert(errMsg(e, "Could not load employee")); }
  };

  return (
    <div className="p-6">
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{profiles.length} employee onboarding{profiles.length !== 1 ? "s" : ""}</div>}
        right={isBoss && <PrimaryButton onClick={() => setEditing({})} testId="hr-onboard-new"><Plus size={12} /> New Onboarding</PrimaryButton>}
      />
      {loading ? <SkeletonRows /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {profiles.map(emp => {
            const total = emp.steps?.length || 0;
            const done = emp.steps_done || 0;
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <div key={emp.id} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4" data-testid={`hr-onboard-${emp.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <EmployeePhoto emp={emp} size={52} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800">{emp.name}</div>
                    <div className="text-[11px] text-slate-500">{[emp.designation, emp.department].filter(Boolean).join(" · ") || "—"}</div>
                    <div className="text-[11px] text-slate-500 mono">{emp.email || "no HEXA login"}{emp.start_date ? ` · joined ${emp.start_date}` : ""}</div>
                  </div>
                  {isBoss && (
                    <div className="flex gap-1">
                      <IconBtn onClick={() => edit(emp)} title="Edit / upload documents" color="#6366f1" testId={`hr-onboard-edit-${emp.id}`}><Pencil size={13} /></IconBtn>
                      <IconBtn onClick={() => remove(emp)} title="Delete profile" color="#94a3b8"><Trash2 size={13} /></IconBtn>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
                  <InfoCell label="Aadhaar" value={emp.aadhaar_number || emp.aadhaar_masked} />
                  <InfoCell label="PAN" value={emp.pan_number || emp.pan_masked} />
                  <InfoCell label="Emergency contact" value={emp.emergency_name ? `${emp.emergency_name}${emp.emergency_relation ? ` (${emp.emergency_relation})` : ""}` : ""} />
                  <InfoCell label="Emergency number" value={emp.emergency_phone ? <span className="inline-flex items-center gap-1"><Phone size={10} />{emp.emergency_phone}</span> : ""} />
                </div>
                <div className="mb-3"><DocChips emp={emp} /></div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] mono text-slate-500 tabular-nums">{done}/{total} · {pct}%</div>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(emp.steps || []).map(s => (
                    <button key={s.id} onClick={() => isBoss && toggle(emp, s.id)} data-testid={`hr-onboard-step-${s.id}`} disabled={!isBoss}
                      className="w-full flex items-center gap-2 text-left px-1.5 py-1 rounded hover:bg-slate-50">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${s.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 bg-white"}`}>
                        {s.done ? <CheckSquare size={10} /> : null}
                      </span>
                      <span className={`text-xs ${s.done ? "line-through text-slate-400" : "text-slate-700"}`}>{s.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {profiles.length === 0 && (
            <div className="col-span-full text-center py-10 text-slate-400 text-sm bg-white border border-dashed border-slate-200 rounded-lg">
              No onboardings yet. Click <b>New Onboarding</b> to add an employee with resume, photo, Aadhaar, PAN and emergency contact.
            </div>
          )}
        </div>
      )}
      {editing && <EmployeeModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-slate-400 mono">{label}</div>
      <div className="text-slate-700 font-medium truncate">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Performance — boss scorecard: attendance, punctuality, task delivery
   and review ratings combined into a 0-100 score with an increment
   suggestion. Reviews are stored on the server.
   ───────────────────────────────────────────────────────────── */
const BAND_STYLE = {
  strong:       { bg: "#dcfce7", fg: "#15803d" },
  standard:     { bg: "#dbeafe", fg: "#1d4ed8" },
  modest:       { bg: "#fef3c7", fg: "#b45309" },
  hold:         { bg: "#fee2e2", fg: "#b91c1c" },
  insufficient: { bg: "#f1f5f9", fg: "#64748b" },
};

function Performance() {
  const isBoss = useIsBoss();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [perf, revs] = await Promise.all([
        isBoss ? api.get(`/hr/performance?days=${days}`).then(r => r.data) : Promise.resolve(null),
        api.get("/hr/reviews").then(r => r.data).catch(() => []),
      ]);
      setData(perf); setReviews(revs || []);
    } catch { setData(null); }
    setLoading(false);
  }, [days, isBoss]);
  useEffect(() => { load(); }, [load]);

  const saveReview = async (payload) => {
    try { await api.post("/hr/reviews", payload); setReviewFor(null); load(); }
    catch (e) { alert(errMsg(e, "Could not save review")); }
  };
  const removeReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/hr/reviews/${id}`); load(); } catch (e) { alert(errMsg(e, "Could not delete")); }
  };
  const rows = data?.rows || [];

  return (
    <div className="p-6 space-y-5">
      {isBoss && (
        <>
          <Toolbar
            left={<div className="text-xs text-slate-500">
              Score = attendance {data?.weights?.attendance ?? 35}% · punctuality {data?.weights?.punctuality ?? 15}% · tasks done {data?.weights?.tasks ?? 25}% · review rating {data?.weights?.reviews ?? 25}%
              <span className="text-slate-400"> (missing parts are left out, Sundays and approved leave don&apos;t count as absences)</span>
            </div>}
            right={<>
              <select value={days} onChange={e => setDays(Number(e.target.value))} className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" data-testid="hr-perf-days">
                {[30, 90, 180, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
              </select>
              <PrimaryButton onClick={() => setReviewFor({})} testId="hr-review-new"><Plus size={12} /> New Review</PrimaryButton>
            </>}
          />
          {loading ? <SkeletonRows /> : (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto" data-testid="hr-perf-table">
              <table className="w-full text-sm min-w-[980px]">
                <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-bold px-4 py-2.5">Employee</th>
                    <th className="text-right font-bold px-3 py-2.5">Attendance</th>
                    <th className="text-right font-bold px-3 py-2.5">Avg hrs/day</th>
                    <th className="text-right font-bold px-3 py-2.5">Late</th>
                    <th className="text-right font-bold px-3 py-2.5">Overtime</th>
                    <th className="text-right font-bold px-3 py-2.5">Tasks done</th>
                    <th className="text-left font-bold px-3 py-2.5">Rating</th>
                    <th className="text-left font-bold px-3 py-2.5">Score</th>
                    <th className="text-left font-bold px-3 py-2.5">Increment suggestion</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const band = BAND_STYLE[r.recommendation?.band] || BAND_STYLE.insufficient;
                    return (
                      <tr key={r.email || r.profile_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hr-perf-row-${r.email || r.profile_id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-slate-800">{r.name}</div>
                          <div className="text-[10px] text-slate-500">{[r.designation, r.department].filter(Boolean).join(" · ") || r.email}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {r.attendance_rate == null ? <span className="text-slate-300">—</span> : <>{r.attendance_rate}%</>}
                          <div className="text-[10px] text-slate-400">{r.days_present}/{r.working_days} days{r.leave_days ? ` · ${r.leave_days} leave` : ""}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.avg_hours || "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.late_days}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{r.overtime_hours ? `${r.overtime_hours}h` : "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {r.tasks_total ? `${r.tasks_done}/${r.tasks_total}` : "—"}
                          {r.tasks_overdue ? <div className="text-[10px] text-rose-500">{r.tasks_overdue} overdue</div> : null}
                        </td>
                        <td className="px-3 py-2.5">{r.avg_rating != null ? <Stars value={r.avg_rating} /> : <span className="text-[11px] text-slate-400">No review</span>}</td>
                        <td className="px-3 py-2.5 w-32">
                          {r.score == null ? <span className="text-slate-300">—</span> : (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full" style={{ width: `${r.score}%`, background: band.fg }} /></div>
                              <span className="text-xs font-bold tabular-nums" style={{ color: band.fg }}>{Math.round(r.score)}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded whitespace-nowrap" style={{ background: band.bg, color: band.fg }}>{r.recommendation?.label}</span>
                          {r.monthly_salary && r.score != null && r.score >= 55 ? (
                            <div className="text-[10px] text-slate-500 mt-0.5">on ₹{Number(r.monthly_salary).toLocaleString("en-IN")}/mo</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button onClick={() => setReviewFor(r)} className="text-[11px] font-semibold text-indigo-600 hover:underline whitespace-nowrap" data-testid={`hr-perf-review-${r.email || r.profile_id}`}>+ Review</button>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-400 text-sm">No employees yet — add them from Directory.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mono mb-2 flex items-center gap-1"><TrendingUp size={12} /> {isBoss ? "Performance reviews" : "My performance reviews"}</div>
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-bold px-4 py-2.5">Employee</th>
                <th className="text-left font-bold px-4 py-2.5">Period</th>
                <th className="text-left font-bold px-4 py-2.5">Reviewer</th>
                <th className="text-left font-bold px-4 py-2.5">Rating</th>
                <th className="text-left font-bold px-4 py-2.5">Highlights</th>
                <th className="text-left font-bold px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {reviews.map(r => (
                <tr key={r.id} data-testid={`hr-review-${r.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">{r.employee}</td>
                  <td className="px-4 py-2.5">{r.period}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.reviewer || "—"}</td>
                  <td className="px-4 py-2.5"><Stars value={r.rating} /></td>
                  <td className="px-4 py-2.5 text-slate-600 truncate max-w-[320px]" title={[r.highlights, r.improvements].filter(Boolean).join(" — Improve: ")}>{r.highlights}</td>
                  <td className="px-4 py-2.5">{isBoss && <IconBtn onClick={() => removeReview(r.id)} color="#94a3b8" title="Delete"><Trash2 size={12} /></IconBtn>}</td>
                </tr>
              ))}
              {reviews.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">No performance reviews yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {reviewFor && <ReviewModal people={rows} preset={reviewFor} onSave={saveReview} onClose={() => setReviewFor(null)} />}
    </div>
  );
}

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={12} fill={n <= Math.round(value) ? "#facc15" : "transparent"} className={n <= Math.round(value) ? "text-yellow-400" : "text-slate-300"} />
      ))}
    </div>
  );
}

function ReviewModal({ people, preset, onSave, onClose }) {
  const { user } = useAuth();
  const first = preset?.name ? preset : people[0] || {};
  const [f, setF] = useState({
    key: first.email || first.name || "",
    period: `Q${Math.floor((new Date().getMonth()) / 3) + 1} ${new Date().getFullYear()}`,
    reviewer: user?.name || "", rating: 4, highlights: "", improvements: "",
  });
  const save = () => {
    const p = people.find(x => (x.email || x.name) === f.key) || {};
    if (!p.name) return;
    onSave({ employee: p.name, employee_email: p.email || "", period: f.period, reviewer: f.reviewer, rating: f.rating, highlights: f.highlights, improvements: f.improvements });
  };
  return (
    <Modal title="New Performance Review" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Employee">
          <select className={INPUT} value={f.key} onChange={e => setF({ ...f, key: e.target.value })} data-testid="hr-review-employee">
            {people.map(p => <option key={p.email || p.name} value={p.email || p.name}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Period"><input className={INPUT} value={f.period} onChange={e => setF({ ...f, period: e.target.value })} placeholder="Q1 2026" /></Field>
      </div>
      <Field label="Reviewer"><input className={INPUT} value={f.reviewer} onChange={e => setF({ ...f, reviewer: e.target.value })} /></Field>
      <Field label="Rating">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => setF({ ...f, rating: n })} type="button" className="p-1" data-testid={`hr-review-star-${n}`}>
              <Star size={20} fill={n <= f.rating ? "#facc15" : "transparent"} className={n <= f.rating ? "text-yellow-400" : "text-slate-300"} />
            </button>
          ))}
        </div>
      </Field>
      <Field label="Highlights / Wins"><textarea rows={2} className={INPUT} value={f.highlights} onChange={e => setF({ ...f, highlights: e.target.value })} /></Field>
      <Field label="Areas of improvement"><textarea rows={2} className={INPUT} value={f.improvements} onChange={e => setF({ ...f, improvements: e.target.value })} /></Field>
      <ModalFooter onCancel={onClose} onSave={save} testId="hr-review-save" />
    </Modal>
  );
}

/* Expenses — reimbursement requests (localStorage) */
function Expenses() {
  const [rows, setRows] = useLocalArray("hexa_hr_expenses");
  const [showForm, setShowForm] = useState(false);
  const add = (payload) => { setRows([{ id: `ex-${Date.now()}`, ...payload, status: "pending", created: new Date().toISOString() }, ...rows]); setShowForm(false); };
  const setStatus = (id, status) => setRows(rows.map(r => r.id === id ? { ...r, status } : r));
  const remove = (id) => setRows(rows.filter(r => r.id !== id));

  const totals = useMemo(() => rows.reduce((a, r) => {
    const v = Number(r.amount || 0);
    a.total += v;
    if (r.status === "pending") a.pending += v;
    if (r.status === "reimbursed") a.reimbursed += v;
    return a;
  }, { total: 0, pending: 0, reimbursed: 0 }), [rows]);

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Total claims" value={`₹${totals.total.toLocaleString("en-IN")}`} color="#f97316" icon={Wallet} />
        <StatCard label="Pending" value={`₹${totals.pending.toLocaleString("en-IN")}`} color="#f59e0b" icon={CalendarCheck} />
        <StatCard label="Reimbursed" value={`₹${totals.reimbursed.toLocaleString("en-IN")}`} color="#22c55e" icon={CheckSquare} />
      </div>
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{rows.length} expense claim{rows.length !== 1 ? "s" : ""}</div>}
        right={<PrimaryButton onClick={() => setShowForm(true)} testId="hr-expense-new"><Plus size={12} /> New Claim</PrimaryButton>}
      />
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left font-bold px-4 py-2.5">Employee</th>
              <th className="text-left font-bold px-4 py-2.5">Date</th>
              <th className="text-left font-bold px-4 py-2.5">Category</th>
              <th className="text-left font-bold px-4 py-2.5">Description</th>
              <th className="text-right font-bold px-4 py-2.5">Amount</th>
              <th className="text-left font-bold px-4 py-2.5">Status</th>
              <th className="text-left font-bold px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`hr-expense-${r.id}`} className="border-t border-slate-100">
                <td className="px-4 py-2.5">{r.employee}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.date}</td>
                <td className="px-4 py-2.5 capitalize">{r.category}</td>
                <td className="px-4 py-2.5 text-slate-600 truncate max-w-[240px]" title={r.description}>{r.description}</td>
                <td className="px-4 py-2.5 text-right font-semibold tabular-nums">₹{Number(r.amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1">
                    {r.status === "pending" && (
                      <>
                        <IconBtn onClick={() => setStatus(r.id, "approved")} testId={`hr-expense-approve-${r.id}`} color="#22c55e" title="Approve"><CheckSquare size={12} /></IconBtn>
                        <IconBtn onClick={() => setStatus(r.id, "rejected")} color="#ef4444" title="Reject"><X size={12} /></IconBtn>
                      </>
                    )}
                    {r.status === "approved" && (
                      <IconBtn onClick={() => setStatus(r.id, "reimbursed")} testId={`hr-expense-reimburse-${r.id}`} color="#22c55e" title="Mark reimbursed"><IndianRupee size={12} /></IconBtn>
                    )}
                    <IconBtn onClick={() => remove(r.id)} color="#94a3b8" title="Delete"><Trash2 size={12} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No expense claims yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <ExpenseModal onSave={add} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function ExpenseModal({ onSave, onClose }) {
  const [f, setF] = useState({ employee: "", date: new Date().toISOString().slice(0, 10), category: "travel", description: "", amount: "" });
  return (
    <Modal title="New Expense Claim" onClose={onClose}>
      <Field label="Employee"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.employee} onChange={e => setF({ ...f, employee: e.target.value })} data-testid="hr-expense-employee" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Category">
          <select className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
            <option value="travel">Travel</option>
            <option value="meals">Meals</option>
            <option value="accommodation">Accommodation</option>
            <option value="office">Office supplies</option>
            <option value="training">Training / conference</option>
            <option value="other">Other</option>
          </select>
        </Field>
      </div>
      <Field label="Description"><textarea rows={2} className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="Amount (INR)"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" type="number" min="0" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="hr-expense-amount" /></Field>
      <ModalFooter onCancel={onClose} onSave={() => { if (!f.employee.trim() || !f.amount) return; onSave(f); }} testId="hr-expense-save" />
    </Modal>
  );
}

/* Policies — company docs (localStorage links / text) */
const DEFAULT_POLICIES = [
  { title: "Employee handbook",       category: "General",     desc: "Company mission, culture, code of conduct, benefits overview." },
  { title: "Leave & attendance policy", category: "Time-off",   desc: "Leave types, carry-forward rules, attendance requirements, WFH policy." },
  { title: "POSH policy",             category: "Compliance",  desc: "Prevention of Sexual Harassment at Workplace — internal committee & redressal." },
  { title: "Data security policy",    category: "IT",          desc: "Acceptable use, device security, VPN, incident reporting." },
  { title: "Expense reimbursement",   category: "Finance",     desc: "Categories, limits, approval workflow, receipt requirements." },
  { title: "Performance review",      category: "Growth",      desc: "Quarterly reviews, 360 feedback, promotion & compensation cycle." },
];

function Policies() {
  const [rows, setRows] = useLocalArray("hexa_hr_policies", DEFAULT_POLICIES.map((p, i) => ({ id: `p-${i}`, ...p, link: "" })));
  const [showForm, setShowForm] = useState(false);
  const add = (payload) => setRows([{ id: `p-${Date.now()}`, ...payload }, ...rows]);
  const remove = (id) => setRows(rows.filter(r => r.id !== id));

  return (
    <div className="p-6">
      <Toolbar
        left={<div className="text-xs text-slate-500 mono">{rows.length} polic{rows.length === 1 ? "y" : "ies"}</div>}
        right={<PrimaryButton onClick={() => setShowForm(true)} testId="hr-policy-new"><Plus size={12} /> New Policy</PrimaryButton>}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-col gap-2" data-testid={`hr-policy-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded flex items-center justify-center text-white shrink-0" style={{ background: "#0ea5e9" }}>
                <FileText size={16} />
              </div>
              <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={12} /></button>
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">{p.title}</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-sky-600 mono mt-0.5">{p.category}</div>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">{p.desc}</div>
            {p.link ? (
              <a href={p.link} target="_blank" rel="noreferrer" className="mt-1 text-xs font-semibold text-indigo-600 hover:underline">Open document →</a>
            ) : (
              <div className="text-[10px] mono uppercase tracking-widest text-slate-400">// no link attached</div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center py-10 text-slate-400 text-sm bg-white border border-dashed border-slate-200 rounded-lg">No policies yet.</div>}
      </div>
      {showForm && <PolicyModal onSave={(p) => { add(p); setShowForm(false); }} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function PolicyModal({ onSave, onClose }) {
  const [f, setF] = useState({ title: "", category: "General", desc: "", link: "" });
  return (
    <Modal title="Add Policy Document" onClose={onClose}>
      <Field label="Title"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="hr-policy-title" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
            {["General", "Time-off", "Compliance", "IT", "Finance", "Growth", "Safety"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Link (URL)"><input className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.link} onChange={e => setF({ ...f, link: e.target.value })} placeholder="https://…" /></Field>
      </div>
      <Field label="Description"><textarea rows={3} className="w-full text-sm px-3 py-2 border border-slate-200 rounded outline-none focus:border-indigo-400 bg-white text-slate-800" value={f.desc} onChange={e => setF({ ...f, desc: e.target.value })} /></Field>
      <ModalFooter onCancel={onClose} onSave={() => { if (!f.title.trim()) return; onSave(f); }} testId="hr-policy-save" />
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
   Shared UI primitives + a localStorage array hook.
   ───────────────────────────────────────────────────────────── */
function useLocalArray(key, initial = []) {
  const [rows, setRows] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw);
      return initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(rows)); } catch { /* quota */ }
  }, [key, rows]);
  return [rows, setRows];
}

function Toolbar({ left, right }) {
  return (
    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
      <div>{left}</div>
      <div className="flex items-center gap-2">{right}</div>
    </div>
  );
}

function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testId}
        className="pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded outline-none focus:border-indigo-400 w-60 bg-white" />
    </div>
  );
}

function PrimaryButton({ children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1">
      {children}
    </button>
  );
}

function IconBtn({ children, onClick, color = "#64748b", title, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className="p-1 rounded hover:bg-slate-100 transition"
      style={{ color }}>
      {children}
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:    { bg: "#f59e0b", label: "Pending" },
    approved:   { bg: "#0ea5e9", label: "Approved" },
    rejected:   { bg: "#ef4444", label: "Rejected" },
    paid:       { bg: "#22c55e", label: "Paid" },
    reimbursed: { bg: "#22c55e", label: "Reimbursed" },
  };
  const m = map[String(status).toLowerCase()] || { bg: "#94a3b8", label: status || "—" };
  return <span className="text-[10px] font-bold uppercase tracking-widest text-white px-2 py-0.5 rounded" style={{ background: m.bg }}>{m.label}</span>;
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm flex items-center gap-3">
      <div className="w-9 h-9 rounded flex items-center justify-center text-white" style={{ background: color }}>
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{label}</div>
        <div className="text-lg font-bold mt-0.5" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function Avatar({ name, size = 28 }) {
  const initials = (name || "?").split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  let h = 0; for (const c of name || "?") h = (h * 31 + c.charCodeAt(0)) % 360;
  return <div className="rounded-full text-white font-bold flex items-center justify-center shrink-0" style={{ width: size, height: size, fontSize: Math.max(10, size / 2.8), background: `hsl(${h} 65% 50%)` }}>{initials}</div>;
}

function SkeletonRows() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-center justify-center text-slate-400 text-xs">
      <Loader2 size={12} className="animate-spin mr-2" /> Loading…
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[500] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className={`hexa-force-light hexa-form-colorful bg-white text-slate-800 w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-lg shadow-2xl overflow-hidden`}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-indigo-600" />
            <div className="text-sm font-bold text-slate-800">{title}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[78vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSave, busy, testId }) {
  return (
    <div className="pt-2 flex justify-end gap-2 border-t border-slate-100 mt-2">
      <button onClick={onCancel} className="text-xs font-semibold px-4 py-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
      <button onClick={onSave} disabled={busy} data-testid={testId}
        className="text-xs font-semibold px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1 disabled:opacity-60">
        {busy && <Loader2 size={12} className="animate-spin" />} Save
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mono mb-1">{label}</div>
      {children}
    </div>
  );
}
